// #region Imports
import { Dialog, TextArea } from '@radix-ui/themes';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FaArrowLeft, FaTimes } from 'react-icons/fa';
import { AZButton } from '../AZButton';
import './AZFeedback.css';
import { FormPart } from './FormPart';
import { TimeLine } from './TimeLine';
// #endregion

// #region Types
interface FeedbackScreenProps {
  isManual: boolean;
  isLoading: boolean;
  onValidateStep: () => Promise<boolean>;
  onGoBack: () => void;
  onClose: () => void;
}
// #endregion

// #region Constants
const STEPS = ['Feature', 'Ergonomics', 'Bugs', 'General'] as const;
const LAST_STEP = STEPS.length - 1;
// #endregion

// #region Component
const FeedbackScreen = ({
  isManual,
  isLoading,
  onValidateStep: _onValidateStep,
  onGoBack,
  onClose,
}: FeedbackScreenProps) => {
  const [formPartFocused, setFormPartFocused] = useState(0);
  const stepDirectionRef = useRef<'forward' | 'backward'>('forward');

  const {
    register,
    formState: { errors },
    trigger,
    clearErrors,
  } = useFormContext();

  const submitRootError = errors.root?.submit;

  // #region Step field names for per-step validation
  const stepFields: Record<number, string[]> = {
    0: ['features', 'featuresComment'],
    1: ['ergonomics', 'ergonomicsComment'],
    2: ['bugsComment'],
    3: [],
  };
  // #endregion

  // #region Navigation handlers
  const handleNext = async () => {
    // Clear any stale submit error so it does not persist across step navigation.
    clearErrors('root.submit');

    const fieldsToValidate = stepFields[formPartFocused] ?? [];
    const isValid =
      fieldsToValidate.length > 0
        ? await trigger(fieldsToValidate as Parameters<typeof trigger>[0])
        : true;

    if (isValid) {
      stepDirectionRef.current = 'forward';
      setFormPartFocused((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    // Clear any stale submit error so it does not persist after navigating back.
    clearErrors('root.submit');
    stepDirectionRef.current = 'backward';
    setFormPartFocused((prev) => prev - 1);
  };
  // #endregion

  const isFirstStep = formPartFocused === 0;
  const isLastStep = formPartFocused === LAST_STEP;

  return (
    <>
      {/* #region Header */}
      <div className="az-feedback-header">
        <div className="az-feedback-title-group">
          <Dialog.Title
            className="az-feedback-title"
            style={{ color: 'var(--az-palette-ice-white)' }}
          >
            Feedback
          </Dialog.Title>
          {!isManual && (
            <p className="az-feedback-disclaimer" style={{ color: 'var(--az-palette-ice-white)' }}>
              Thank you for using this app. Could you take a moment to give us your feedback?
            </p>
          )}
        </div>
        <div className="az-feedback-header-buttons">
          {isManual && (
            <AZButton
              type="button"
              variant="ghost"
              description="Go back to menu"
              className="az-feedback-icon-button"
              onClick={onGoBack}
              aria-label="Go back to menu"
            >
              <FaArrowLeft aria-hidden="true" />
            </AZButton>
          )}
          <AZButton
            variant="ghost"
            className="az-feedback-icon-button"
            onClick={onClose}
            aria-label="Close"
            icon={<FaTimes aria-hidden="true" />}
          ></AZButton>
        </div>
      </div>
      {/* #endregion */}

      {/* #region Timeline */}
      <TimeLine steps={[...STEPS]} currentStep={formPartFocused} />
      {/* #endregion */}

      {/* #region Step content */}
      <div className="az-feedback-form">
        {formPartFocused === 0 && (
          <div key="step-0" className={stepDirectionRef.current === 'forward' ? 'az-feedback-step-enter-forward' : 'az-feedback-step-enter-backward'}>
            <FormPart
              name="features"
              title="Features"
              description="Are all the features you need present?"
              commentDescription="An idea for a feature to add?"
              lowerNoteDescription="Nothing"
              upperNoteDescription="Everything"
            />
          </div>
        )}
        {formPartFocused === 1 && (
          <div key="step-1" className={stepDirectionRef.current === 'forward' ? 'az-feedback-step-enter-forward' : 'az-feedback-step-enter-backward'}>
            <FormPart
              name="ergonomics"
              title="Ergonomics"
              description="Is the application intuitive?"
              commentDescription="What caused you problems?"
              lowerNoteDescription="Not at all"
              upperNoteDescription="Very intuitive"
            />
          </div>
        )}
        {formPartFocused === 2 && (
          <div key="step-2" className={stepDirectionRef.current === 'forward' ? 'az-feedback-step-enter-forward' : 'az-feedback-step-enter-backward'}>
            <FormPart
              name="bugs"
              title="Bugs"
              description="Do you encounter bugs often?"
              commentDescription="Can you explain them?"
              required={false}
              lowerNoteDescription="Never"
              upperNoteDescription="All the time"
            />
          </div>
        )}
        {formPartFocused === 3 && (
          <div key="step-3" className={stepDirectionRef.current === 'forward' ? 'az-feedback-step-enter-forward' : 'az-feedback-step-enter-backward'}>
            <div>
              <label className="az-feedback-label" htmlFor="globalComment">
                General comment (optional)
              </label>
              <TextArea
                id="globalComment"
                placeholder="Any other thoughts you would like to share?"
                {...register('globalComment')}
              />
              {errors['globalComment'] && (
                <p className="az-feedback-error-msg" role="alert">
                  {String(errors['globalComment']?.message ?? 'Error')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      {/* #endregion */}

      {/* #region Footer navigation */}
      <div className="az-feedback-screen-footer">
        <div>
          {!isFirstStep && (
            <AZButton type="button" variant="secondary" onClick={handleBack}>
              Back
            </AZButton>
          )}
        </div>
        <div className="az-feedback-screen-footer-right">
          {!isLastStep && (
            <AZButton type="button" variant="primary" onClick={handleNext}>
              Next
            </AZButton>
          )}
          {isLastStep && (
            <>
              {submitRootError && (
                <p className="az-feedback-error-msg" role="alert">
                  {String(submitRootError.message ?? 'Failed to send. Please try again.')}
                </p>
              )}
              <AZButton type="submit" variant="primary" disabled={isLoading} isLoading={isLoading}>
                Submit
              </AZButton>
            </>
          )}
        </div>
      </div>
      {/* #endregion */}
    </>
  );
};
// #endregion

// #region Display Name
FeedbackScreen.displayName = 'FeedbackScreen';
// #endregion

export { FeedbackScreen };
